import { useField } from "@formiz/core";
import type { UseFieldProps } from "@formiz/core/dist/types/field.types";
import { Radio, RadioGroup, Spacer } from "@nextui-org/react";
import type { ChangeEvent } from "react";

import { Row, Text } from "@/components/customComponents";

export interface RadioGroupFieldOption {
  value: string;
  label: string;
  isDisabled?: boolean;
}

export interface RadioGroupFieldProps extends UseFieldProps {
  title: string;
  desc?: string;
  options?: RadioGroupFieldOption[];
  orientation?: "horizontal" | "vertical";
  size?: "sm" | "md" | "lg";
  showError?: boolean;
  classNames?: {
    wrapper?: string;
    label?: string;
  };
}

// `showError` is destructured out of `props` so it never reaches `otherProps` and gets spread onto the DOM.
const RadioGroupField = ({ title, desc, size, orientation, options = [], showError = true, ...props }: RadioGroupFieldProps) => {
  const { value, setValue, errorMessage, isValid, otherProps } = useField(props);
  const shouldDisplayError = showError && !isValid;

  return (
    <>
      <Row>
        <p className="leading-6">
          <span className="ml-[2px] font-bold">{title}</span>
          <span className="ml-[12px] text-[13px]">{desc}</span>
        </p>
      </Row>
      <Spacer y={2} />

      <RadioGroup
        {...otherProps}
        value={value ?? undefined}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
        orientation={orientation}
        size={size}
        isInvalid={shouldDisplayError}
      >
        {options.map((option) => (
          <Radio
            value={option.value}
            aria-labelledby={option.value}
            key={option.value}
            isDisabled={option.isDisabled}
            classNames={{ wrapper: "mt-[2px] mr-[2px]", label: "text-[16px]" }}
          >
            {option.label}
          </Radio>
        ))}
      </RadioGroup>
      {shouldDisplayError ? (
        <div data-testid={`RadioGroupField-${props.name}-Error`}>
          <Text type={"error"}>{errorMessage}</Text>
        </div>
      ) : null}
      <Spacer y={6} />
    </>
  );
};

export default RadioGroupField;
